import { FC, useState } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { BookOpen, Plus, Search, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useQuery } from '@tanstack/react-query';
import { supabase } from "@/integrations/supabase/client";
import { LessonCard } from '@/components/admin/lesson/LessonCard';
import { toast } from "@/components/ui/use-toast";

const AdminLessons: FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { data: lessons, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-lessons'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('lessons')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching lessons:', error);
        toast({
          title: "Error",
          description: "No se pudieron cargar las lecciones",
          variant: "destructive",
        });
        throw error;
      }

      return data;
    },
  });

  const filteredLessons = lessons?.filter(lesson =>
    lesson.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    lesson.content_text.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetch();
      toast({
        title: "Actualizado",
        description: "Lista de lecciones actualizada correctamente",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo actualizar la lista de lecciones",
        variant: "destructive",
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleLessonDeleted = () => {
    console.log('Refreshing lessons list after deletion');
    refetch();
  };

  return (
    <AdminLayout>
      <div className="container mx-auto px-4 py-4 md:py-8 max-w-7xl">
        <div className="flex items-center mb-8">
          <div className="flex items-center gap-3">
            <BookOpen className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-bold text-gray-900">Administración de Lecciones</h1>
          </div>
        </div>

        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              type="text"
              placeholder="Buscar lecciones..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-full"
            />
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 md:mb-8">
          <div className="flex gap-2">
            <Button 
              variant="outline"
              size="icon"
              onClick={handleRefresh}
              disabled={isRefreshing}
              className={isRefreshing ? 'animate-spin' : ''}
              title="Actualizar lista"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Link to="/admin/lessons/create">
              <Button className="flex items-center gap-2 shadow-sm">
                <Plus className="h-4 w-4" />
                Nueva Lección
              </Button>
            </Link>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="ml-3 text-gray-600">Cargando lecciones...</p>
          </div>
        ) : error ? (
          <div className="text-center py-8 px-4 bg-red-50 rounded-lg">
            <p className="text-red-600">Error al cargar las lecciones. Por favor, intente nuevamente.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
            {filteredLessons?.map((lesson) => (
              <LessonCard 
                key={lesson.id} 
                lesson={lesson} 
                onDelete={handleLessonDeleted}
              />
            ))}
            {filteredLessons?.length === 0 && (
              <div className="col-span-full text-center py-8 bg-gray-50 rounded-lg">
                <p className="text-gray-500">
                  No se encontraron lecciones que coincidan con tu búsqueda
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminLessons;